import { useState } from "react";
import { useT } from "../i18n/hooks.ts";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { SubBasinResult } from "../engine/engine.ts";
import { downloadText, generateDetailCsv } from "../engine/exportCsv.ts";
import { formatScaleDate, type SeriesScale } from "../model/seriesScale.ts";

const COLORS = [
  "#3fa7b3",
  "#e07a5f",
  "#81b29a",
  "#f2cc8f",
  "#9b5de5",
  "#f15bb5",
  "#4361ee",
  "#ef476f",
  "#06d6a0",
  "#ffd166",
];

/**
 * Step-by-step internal detail for a sub-basin (H, S1/S2, ETR, Q0/Q1/Q2,
 * aquifer V...) — the web equivalent of the real .exe's "Partial
 * results"/Sal_parciales.csv (always available, doesn't depend on any
 * checkbox, see calculationOptions.ts). Lets you choose which series to
 * overlay on the same chart, and download the full detail as CSV.
 */
export function DetailChart({
  result,
  dates,
  scale,
}: {
  result: SubBasinResult;
  dates: string[];
  /** The project's own scale (EvalHidProject.seriesScale) — decides how the x-axis's ISO dates are displayed (dd/mm/aaaa or mm/aaaa), same as the input-series charts (SeriesChart.tsx). */
  scale: SeriesScale;
}) {
  const t = useT();
  const seriesNames = Object.keys(result.detail ?? {});
  const [selected, setSelected] = useState<string[]>(seriesNames.slice(0, 2));

  if (seriesNames.length === 0) return null;

  function toggle(name: string) {
    setSelected((previous) =>
      previous.includes(name) ? previous.filter((n) => n !== name) : [...previous, name]
    );
  }

  const data = dates.map((date, i) => {
    const row: Record<string, string | number> = { date };
    for (const name of selected) row[name] = result.detail![name][i];
    return row;
  });

  return (
    <div className="chart chart-detail">
      <h3>{t("detailChart.title", { description: result.description })}</h3>

      <div className="series-selector">
        {seriesNames.map((name) => (
          <label key={name}>
            <input
              type="checkbox"
              checked={selected.includes(name)}
              onChange={() => toggle(name)}
            />
            {name}
          </label>
        ))}
      </div>

      <button
        className="button"
        onClick={() =>
          downloadText(`Sal_parciales_${result.description}.csv`, generateDetailCsv(dates, result))
        }
      >
        {t("common.downloadCsv")}
      </button>

      {selected.length > 0 && (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={(iso: string) => formatScaleDate(iso, scale)} />
            <YAxis />
            <Tooltip />
            <Legend />
            {selected.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={COLORS[i % COLORS.length]}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
